/**
 * Gap Assessments Card
 * Displays business and personal assessment scores
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionContext';
import { formatPercentage, getScoreColor } from '../../utils/enhancedDashboardUtils';

const GapAssessmentsCard = ({ assessment, personalAssessment }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const canViewBusiness = hasPermission('can_view_gap_assessments');
  const canViewPersonal = hasPermission('can_view_personal_gap');

  // Don't show card if viewer doesn't have any permissions
  if (!canViewBusiness && !canViewPersonal) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-green-600 p-4 text-white">
        <div className="flex items-center space-x-3">
          <Target size={24} />
          <div>
            <h3 className="text-lg font-bold">Gap Assessments</h3>
            <p className="text-xs text-blue-100">Business & Personal</p>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {/* Business Assessment */}
        {canViewBusiness && (
          <div className={canViewPersonal ? "border-b border-gray-200 pb-3" : "pb-3"}>
            <p className="text-xs font-semibold text-gray-600 mb-2">BUSINESS</p>
            {assessment && assessment.answered_questions > 0 ? (
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="text-center p-2.5 bg-blue-50 rounded">
                  <p className={`text-xl font-bold ${getScoreColor(assessment.overall_score)}`}>
                    {formatPercentage(assessment.overall_score)}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">Overall</p>
                </div>
                <div className="text-center p-2.5 bg-blue-50 rounded">
                  <p className={`text-xl font-bold ${getScoreColor(assessment.attractiveness_score || 0)}`}>
                    {formatPercentage(assessment.attractiveness_score || 0)}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">Attractive</p>
                </div>
                <div className="text-center p-2.5 bg-blue-50 rounded">
                  <p className={`text-xl font-bold ${getScoreColor(assessment.readiness_score || 0)}`}>
                    {formatPercentage(assessment.readiness_score || 0)}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">Readiness</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500 text-center py-3">Not started</p>
            )}
          </div>
        )}

        {/* Personal Assessment */}
        {canViewPersonal && (
          <div className="pb-2">
            <p className="text-xs font-semibold text-gray-600 mb-2">PERSONAL</p>
            {personalAssessment && personalAssessment.answered_questions > 0 ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2.5 bg-green-50 rounded">
                    <p className={`text-xl font-bold ${getScoreColor(personalAssessment.overall_score)}`}>
                      {formatPercentage(personalAssessment.overall_score)}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">Overall</p>
                  </div>
                  <div className="text-center p-2.5 bg-green-50 rounded">
                    <p className={`text-xl font-bold ${getScoreColor(personalAssessment.financial_score || 0)}`}>
                      {formatPercentage(personalAssessment.financial_score || 0)}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">Financial</p>
                  </div>
                  <div className="text-center p-2.5 bg-green-50 rounded">
                    <p className={`text-xl font-bold ${getScoreColor(personalAssessment.wellbeing_score || 0)}`}>
                      {formatPercentage(personalAssessment.wellbeing_score || 0)}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">Wellbeing</p>
                  </div>
                  <div className="text-center p-2.5 bg-green-50 rounded">
                    <p className={`text-xl font-bold ${getScoreColor(personalAssessment.spiritual_score || 0)}`}>
                      {formatPercentage(personalAssessment.spiritual_score || 0)}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">Spiritual</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500 text-center py-3">Not started</p>
            )}
          </div>
        )}

        {/* Spacer to align buttons with other widgets */}
        <div className="h-2"></div>

        {/* View Assessments Buttons */}
        <div className={`grid ${canViewBusiness && canViewPersonal ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
          {canViewBusiness && (
            <button
              onClick={() => navigate('/gap-analysis')}
              className="px-3 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition text-xs min-h-[44px]"
            >
              Business
            </button>
          )}
          {canViewPersonal && (
            <button
              onClick={() => navigate('/personal-gap-analysis')}
              className="px-3 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition text-xs min-h-[44px]"
            >
              Personal
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

GapAssessmentsCard.propTypes = {
  assessment: PropTypes.shape({
    answered_questions: PropTypes.number,
    overall_score: PropTypes.number,
    attractiveness_score: PropTypes.number,
    readiness_score: PropTypes.number
  }),
  personalAssessment: PropTypes.shape({
    answered_questions: PropTypes.number,
    overall_score: PropTypes.number,
    financial_score: PropTypes.number,
    wellbeing_score: PropTypes.number,
    spiritual_score: PropTypes.number
  })
};

export default GapAssessmentsCard;
